import React, { useState, useEffect } from "react";
import { Box, Typography, useMediaQuery, Card, Divider } from "@mui/material";
import { ErrorMessage, Field } from "formik";
import { useTheme } from "@mui/material/styles";

// Local Imports
import AdxDropdown from "components/custom/AdxDropdown";
import AdxMenuItem from "components/custom/AdxMenuItem";
import AdxErrorMessage from "components/custom/AdxErrorMessage";
import AdxAvatarSide from "../../adminLeaveSetting/components/AdxAvatarSide";
import Loading from "components/dumb/loading/Loading";

// API services
import { useGetApprovalRoute } from "services/api/modules/leave/leavePolicy/approvalGroup";

const PolicyApproval = ({ formikBag }) => {
  const { values, handleChange } = formikBag;
  let theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("lg"));
  const [selectedApprovalRoute, setSelectedApprovalRoute] = useState({});
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const { data: options = [], isLoading } = useGetApprovalRoute();

  const getRouteLayers = (route) => {
    if (!route) return [];

    const layers = [
      route.layer_one,
      route.layer_two,
      route.layer_three,
      route.layer_four,
      route.layer_five,
    ];

    return layers.slice(0, Number(route.num_layers) || 0).filter(Boolean);
  };

  const formatMembers = (members = []) => {
    if (!members.length) return "No members configured";

    return members.map((employee, index, arr) => {
      return index === arr.length - 1
        ? `${employee.name}.`
        : index === arr.length - 2
        ? `${employee.name} & `
        : `${employee.name}, `;
    });
  };

  useEffect(() => {
    const selected = options?.find(
      (option) => option.id === values.approval_route_id
    );
    setSelectedApprovalRoute(selected);
    setSelectedRoutes(getRouteLayers(selected));
  }, [values.approval_route_id, options]);

  if (isLoading) {
    return <Loading show={isLoading} />;
  }

  return (
    <Box mt={2} display="flex" flexDirection="column" rowGap={4}>
      <Field
        as={AdxDropdown}
        name="approval_route_id"
        value={values.approval_route_id}
        onChange={(event) => {
          handleChange(event);
        }}
        label="Approval route"
        sx={{ width: { xs: 1, sm: 0.6 } }}
        errorMessage={
          <ErrorMessage name="approval_route_id" component={AdxErrorMessage} />
        }
      >
        {options.map((option) => (
          <AdxMenuItem
            key={option.id}
            value={option.id}
            selectedvalue={values.approval_route_id}
          >
            {option.name}
          </AdxMenuItem>
        ))}
      </Field>
      {values.approval_route_id && (
        <>
          <Box display="flex" alignItems="center" columnGap={2}>
            <Typography>Route</Typography>
            <Typography color="light-gray-text.main">
              {selectedApprovalRoute?.num_layers === 1
                ? `${selectedApprovalRoute?.num_layers} layer`
                : `${selectedApprovalRoute?.num_layers} layers`}
            </Typography>
          </Box>
          <Card sx={{ borderRadius: 2 }} variant="outlined">
            {selectedRoutes?.map((list, index) => (
              <React.Fragment key={index}>
                <Box
                  p={2}
                  display={"flex"}
                  alignItems={"center"}
                  width={"100%"}
                >
                  <Box
                    sx={{
                      minWidth: 1 / 10,
                      mr: { xs: 2, sm: null },
                    }}
                  >
                    <Typography variant="body1" color="light-gray-text.main">
                      Layer {index + 1}
                    </Typography>
                  </Box>

                  {matches ? (
                    <Box width={1 / 8}>
                      <AdxAvatarSide
                        active
                        data={list.members || []}
                        imageData={list.profile_image_url}
                      />
                    </Box>
                  ) : null}
                  <Box display={"flex"} flexDirection={"column"} width={6 / 8}>
                    <Typography
                      noWrap
                      variant="body1"
                      color="dark-gray-text.main"
                    >
                      {list.name}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="light-gray-text.main"
                    >
                      {formatMembers(list.members)}
                    </Typography>
                  </Box>
                </Box>

                <Divider variant="full" />
              </React.Fragment>
            ))}
          </Card>
        </>
      )}
    </Box>
  );
};

export default PolicyApproval;
